import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { motion } from 'framer-motion';
import { UserPlus, X, FileDown, Wifi, PenLine, Gamepad2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClassDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cls, setCls] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStudent, setNewStudent] = useState('');
  const [adding, setAdding] = useState(false);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchData();
  }, [token, id]);

  const fetchData = async () => {
    try {
      const [clsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/classes/${id}`, { headers }),
        fetch(`${BACKEND_URL}/api/classes/${id}/stats`, { headers })
      ]);
      if (clsRes.ok) setCls(await clsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const addStudent = async () => {
    if (!newStudent.trim() || !cls) return;
    setAdding(true);
    const updated = [...(cls.students || []), newStudent.trim()];
    try {
      const res = await fetch(`${BACKEND_URL}/api/classes/${id}`, { method: 'PUT', headers, body: JSON.stringify({ students: updated }) });
      if (res.ok) { setNewStudent(''); fetchData(); }
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const removeStudent = async (name) => {
    if (!cls) return;
    const updated = (cls.students || []).filter(s => s !== name);
    try {
      await fetch(`${BACKEND_URL}/api/classes/${id}`, { method: 'PUT', headers, body: JSON.stringify({ students: updated }) });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const exportPDF = async () => {
    const { default: jsPDF } = await import('jspdf');
    const doc = new jsPDF();
    doc.setFontSize(22);
    doc.text(`PANTASS - ${t('classReport', language)}`, 20, 20);
    doc.setFontSize(14);
    doc.text(`${t('className', language)}: ${stats?.class_name || ''}`, 20, 32);
    doc.text(`${t('date', language)}: ${new Date().toLocaleDateString()}`, 20, 40);
    doc.setFontSize(11);
    let y = 55;
    doc.text(t('studentName', language), 20, y);
    doc.text(t('totalGames', language), 80, y);
    doc.text(t('correct', language), 110, y);
    doc.text(t('wrong', language), 135, y);
    doc.text(t('accuracy', language), 155, y);
    doc.text(t('winsCount', language), 180, y);
    y += 8;
    doc.line(20, y - 3, 195, y - 3);
    (stats?.students || []).forEach(s => {
      doc.text(s.name, 20, y);
      doc.text(String(s.total_games), 80, y);
      doc.text(String(s.total_correct), 110, y);
      doc.text(String(s.total_wrong), 135, y);
      doc.text(`${s.accuracy}%`, 155, y);
      doc.text(String(s.wins), 180, y);
      y += 7;
    });
    doc.save(`PANTASS_${stats?.class_name || 'Class'}_Report.pdf`);
  };

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
        </button>
        <h1 className="dashboard-header-title">{cls?.name || t('classDetail', language)}</h1>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {loading ? (
          <div className="lb-loading"><div className="lb-spinner" /></div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Add Student */}
            <div className="class-add-student">
              <input className="setup-input setup-input-team1" type="text" value={newStudent}
                onChange={e => setNewStudent(e.target.value)} placeholder={t('addStudentPlaceholder', language)}
                maxLength={25} data-testid="add-student-input"
                onKeyDown={e => e.key === 'Enter' && addStudent()} />
              <button className="class-add-btn" onClick={addStudent} disabled={adding} data-testid="add-student-btn">
                <UserPlus size={18} /> {t('addStudent', language)}
              </button>
            </div>

            {/* Quick Actions */}
            <div className="class-actions" data-testid="class-actions">
              <button className="class-action-btn class-action-live" data-testid="class-start-live"
                onClick={() => navigate('/classroom', { state: { classId: id, className: cls?.name, students: cls?.students || [] } })}>
                <Wifi size={18} />
                <span>{language === 'en' ? 'Start Live Session' : 'Mula Sesi Live'}</span>
              </button>
              <button className="class-action-btn class-action-game" data-testid="class-start-game"
                onClick={() => navigate('/setup', { state: { subjectId: 'matematik', subjectType: 'math', classStudents: cls?.students || [], className: cls?.name } })}>
                <Gamepad2 size={18} />
                <span>{language === 'en' ? '2-Player Game' : 'Game 2 Pemain'}</span>
              </button>
              <button className="class-action-btn class-action-questions"
                onClick={() => navigate('/custom-questions')}>
                <PenLine size={18} />
                <span>{language === 'en' ? 'Custom Questions' : 'Soalan Tersuai'}</span>
              </button>
            </div>

            {/* Students Table */}
            {stats && stats.students.length > 0 ? (
              <div className="lb-table-container">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('studentName', language)}</th>
                      <th>{t('totalGames', language)}</th>
                      <th>{t('correct', language)}</th>
                      <th>{t('wrong', language)}</th>
                      <th>{t('accuracy', language)}</th>
                      <th>{t('winsCount', language)}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.students.map((s, i) => (
                      <tr key={s.name}>
                        <td>{i + 1}</td>
                        <td className="lb-team-name">{s.name}</td>
                        <td>{s.total_games}</td>
                        <td className="lb-wins">{s.total_correct}</td>
                        <td style={{ color: 'hsl(0 84% 56%)' }}>{s.total_wrong}</td>
                        <td><strong>{s.accuracy}%</strong></td>
                        <td className="lb-total-score">{s.wins}</td>
                        <td><button className="class-remove-btn" onClick={() => removeStudent(s.name)}><X size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="lb-empty">{t('noStudents', language)}</div>
            )}

            {/* Export */}
            {stats && stats.students.length > 0 && (
              <button className="export-pdf-btn" onClick={exportPDF} data-testid="class-export-pdf" style={{ marginTop: '20px' }}>
                <FileDown size={18} style={{ marginRight: '8px', display: 'inline' }} />
                {t('exportClassReport', language)}
              </button>
            )}

            <button className="setup-back-btn" onClick={() => navigate('/classes')} style={{ marginTop: '12px' }}>&larr; {t('backToClasses', language)}</button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
